// A refusal shown on a candidate screen is his rule sentence, looked up by the refusal's rule code (and, where one
// code guards several fields, by code@field); the server's own detail text is never rendered. tests/mc/
// b3-refusals.test.mjs reads every rule code the candidate master tools raise and proves each has a sentence here.

import { AppError } from "@/api/runtime";
import { G6_RULE_LABELS } from "@/mc";
import { BRD } from "./brd";
import { CANDIDATE_RULES, PROFILE_RULES } from "./labels";

/** The rule code of a RULE_UNMET refusal ("RULE_UNMET / potential_matches: …" → "potential_matches"). */
export function ruleCode(error: AppError): string | null {
  const match = /RULE_UNMET \/ ([a-z_]+)/.exec(error.message);
  return match?.[1] ?? null;
}

/** Rule code (or code@field) → his sentence. */
export const REFUSAL_SENTENCES: Readonly<Record<string, string>> = {
  potential_matches: CANDIDATE_RULES.potentialMatch,
  decision_required: CANDIDATE_RULES.potentialMatch,
  resolution_not_permitted: CANDIDATE_RULES.potentialMatch,
  "reason_required@decision.reason": CANDIDATE_RULES.potentialMatch,
  "reason_required@no_email_reason": CANDIDATE_RULES.noEmail,
  reason_required: CANDIDATE_RULES.manualEntry,
  email_or_exception: CANDIDATE_RULES.manualEntry,
  exception_sourced_only: CANDIDATE_RULES.noEmail,
  name_required: CANDIDATE_RULES.manualEntry,
  source: CANDIDATE_RULES.manualEntry,
  owner_required: CANDIDATE_RULES.manualEntry,
  owner_not_recruiter: CANDIDATE_RULES.manualEntry,
  contact_invalid: CANDIDATE_RULES.manualEntry,
  confirmation_required: CANDIDATE_RULES.humanConfirmation,
  parse_status: CANDIDATE_RULES.humanConfirmation,
  parse_open: CANDIDATE_RULES.proposedForReview,
  file_not_scanned: BRD.files,
  resume_type: BRD.files,
  requisition_required: BRD.links,
  requisition_status: BRD.links,
  already_in_pipeline: BRD.links,
  merged_into: BRD.links,
  recruiter_unassigned: G6_RULE_LABELS.taManagerAssigns,
  // the profile, Duplicate review, merge and recovery (S12)
  "reason_required@reason": BRD.audited,
  stale_version: PROFILE_RULES.optimisticConcurrency,
  no_change: PROFILE_RULES.materialUpdates,
  owner_change_not_permitted: CANDIDATE_RULES.manualEntry,
  already_resolved: PROFILE_RULES.closeWhenResolved,
  same_profile: BRD.mergePreservesHistory,
  not_active: BRD.adminMerges,
  has_merged_records: BRD.adminMerges,
  both_in_loop: BRD.links,
  nothing_to_keep: BRD.mergePreservesHistory,
  not_merged: PROFILE_RULES.mergeRecovery,
};

/** His sentence for a 4xx refusal, or null when the refusal is not a rule (a 5xx or a lost connection is an error). */
export function refusalSentence(error: unknown): string | null {
  if (!(error instanceof AppError) || error.status < 400 || error.status >= 500) return null;
  const code = ruleCode(error);
  if (code) {
    for (const field of Object.keys(error.fieldErrors)) {
      const exact = REFUSAL_SENTENCES[`${code}@${field}`];
      if (exact) return exact;
    }
    if (REFUSAL_SENTENCES[code]) return REFUSAL_SENTENCES[code];
  }
  return CANDIDATE_RULES.recruiterCreates;
}
